// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ClientOrderService, { ClientInquiry } from '../../service/clientOrder.service';

export default function InquiriesPage() {
  const [inquiries, setInquiries] = useState<ClientInquiry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [selectedInquiry, setSelectedInquiry] = useState<ClientInquiry | null>(null);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const data = await ClientOrderService.getInquiries();
      setInquiries(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const filtered = inquiries.filter((inq) =>
    inq.inquiryCode.toLowerCase().includes(search.toLowerCase()) ||
    inq.subject.toLowerCase().includes(search.toLowerCase()) ||
    inq.productName.toLowerCase().includes(search.toLowerCase()) ||
    inq.supplierName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <QuestionAnswerIcon sx={{ color: '#A77B58', fontSize: 26 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#3B3027' }}>
              Follow-Ups • Inquiries
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '13px', color: '#756B63', mt: 0.5 }}>
            Sub-menu tracking client inquiries, product requests, and supplier responses
          </Typography>
        </Box>

        <Chip
          label={`${inquiries.length} Inquiries Logged`}
          sx={{ bgcolor: '#FAF6F0', color: '#5F4B3B', fontWeight: 700, border: '1px solid #E8DFD5' }}
        />
      </Box>

      {/* Search Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid #E8DFD5',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <TextField
          placeholder="Search inquiries by code, subject, product, supplier..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 500 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#A77B58' }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="outlined"
          size="small"
          onClick={fetchInquiries}
          startIcon={<RefreshIcon />}
          sx={{
            borderColor: '#D6CEC4',
            color: '#5F4B3B',
            textTransform: 'none',
            fontWeight: 700,
          }}
        >
          Refresh
        </Button>
      </Paper>

      {/* Inquiries Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ borderRadius: 3, border: '1px solid #E8DFD5', overflow: 'hidden' }}
      >
        <Table sx={{ minWidth: 800 }}>
          <TableHead sx={{ bgcolor: '#FAF6F0' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Inquiry ID</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Subject & Product</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Quantity & Target Price</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Target Supplier</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px', textAlign: 'center' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress sx={{ color: '#A77B58' }} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: '#756B63' }}>No inquiries found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ fontWeight: 700, fontSize: '13px' }}>
                    {item.inquiryCode}
                    <Typography sx={{ fontSize: '11px', color: '#9E948C' }}>{item.createdAt}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, fontSize: '13.5px', color: '#3B3027' }}>
                      {item.subject}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#756B63' }}>
                      Product: {item.productName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{item.quantity}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: '#059669', fontWeight: 700 }}>
                      Target: {item.price}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: '#3B3027' }}>
                    {item.supplierName}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status}
                      size="small"
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        bgcolor: item.status === 'Replied' ? '#ecfdf5' : '#fef3c7',
                        color: item.status === 'Replied' ? '#059669' : '#b45309',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSelectedInquiry(item)}
                      startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
                      sx={{
                        borderColor: '#D6CEC4',
                        color: '#5F4B3B',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '11.5px',
                        '&:hover': { borderColor: '#A77B58', bgcolor: 'rgba(167, 123, 88, 0.05)' },
                      }}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Inquiry Detail Modal */}
      <Dialog
        open={Boolean(selectedInquiry)}
        onClose={() => setSelectedInquiry(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        {selectedInquiry && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{selectedInquiry.inquiryCode}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63' }}>Date: {selectedInquiry.createdAt}</Typography>
              </Box>
              <IconButton onClick={() => setSelectedInquiry(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: '#E8DFD5' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#3B3027', mb: 1 }}>
                {selectedInquiry.subject}
              </Typography>
              <Box sx={{ p: 2, bgcolor: '#FAF6F0', borderRadius: 2, mb: 2 }}>
                <Typography sx={{ fontSize: '12px', color: '#756B63' }}>
                  <strong>Product:</strong> {selectedInquiry.productName}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  <strong>Quantity Required:</strong> {selectedInquiry.quantity}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  <strong>Target Price:</strong> {selectedInquiry.price}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  <strong>Target Supplier:</strong> {selectedInquiry.supplierName}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '13px', mb: 0.5 }}>Message Body:</Typography>
              <Typography sx={{ fontSize: '13px', color: '#5F4B3B', lineHeight: 1.6 }}>
                {selectedInquiry.message}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setSelectedInquiry(null)} sx={{ textTransform: 'none' }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
